import React, { useCallback, useState } from 'react';
import { Brushaway } from '../../molecules/Brushaway/Brushaway';
import { AddAttachmentsBrushawayPanel } from './components/AddAttachmentsBrushawayPanel';
import './AddAttachmentsBrushawayStyles.scss';
/**
 * `minWidth={960}` is not arbitrary -- `docs/ai-knowledge/ai-patterns/mistake-log.md` (2026-09-22)
 * records shipping a Brushaway with a photo/checkbox table at 720 and having the checkbox collide
 * with the thumbnail. A Brushaway with a queue table carrying inline-edit controls uses 960.
 */
const MIN_WIDTH = 960;
/**
 * "Add attachments" upload drawer. Storage-agnostic: this organism never owns the upload queue
 * itself (no `useDirectUpload`, no S3/contract types) -- the consumer owns that state one level
 * above (its "Page"), passing queue items and actions down as props, so the queue survives this
 * drawer closing (background upload continuation is the consuming feature's concern, e.g.
 * `building-attachment-direct-uploads`'s task-22). This organism is only a view onto that state
 * plus the drawer's own close/discard-confirm choreography.
 *
 * Close behaviour (E2E-18):
 * - uploading -> close to background, fire `onCloseToBackground` (the consumer's own progress
 *   toast reacts to this)
 * - staged queue, not uploading -> "Discard files?" confirm
 * - empty queue -> close immediately
 *
 * Implementation note on the discard-confirm: `Brushaway`'s built-in header close button always
 * calls its own `closeBrushaway()` unconditionally (there is no "veto this close" hook in its
 * public API), so clicking X hides the panel immediately regardless of what this component
 * decides. When we want to keep the queue, `AddAttachmentsBrushawayPanel` (rendered inside
 * `Brushaway`'s own provider, where `useBrushaway()` is reachable) calls `openBrushaway()` to bring
 * the panel back once the user cancels the discard -- see that file. `Brushaway` itself is a
 * frozen `@ui` molecule and out of this organism's file ownership, so this works around the gap
 * rather than changing it.
 */
export function AddAttachmentsBrushaway({ open, items, isUploading, aggregateProgress, onFilesSelected, onNameChange, onRemoveItem, onRetryItem, onRetryFailed, onCancelUpload, onSubmit, onClose, onCloseToBackground, maxSizeBytes, accept, columns, labels, className }) {
    const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
    const requestClose = useCallback(() => {
        if (isUploading) {
            onCloseToBackground();
            return;
        }
        if (items.length > 0) {
            setDiscardConfirmOpen(true);
            return;
        }
        onClose();
    }, [isUploading, items.length, onClose, onCloseToBackground]);
    const handleConfirmDiscard = useCallback(() => {
        setDiscardConfirmOpen(false);
        onClose();
    }, [onClose]);
    const handleCancelDiscard = useCallback(() => {
        setDiscardConfirmOpen(false);
    }, []);
    const rootClassName = ['qmb-ui-add-attachments-brushaway', className].filter(Boolean).join(' ');
    return (React.createElement(Brushaway, { controlledOpen: open, onClose: requestClose, minWidth: MIN_WIDTH, className: rootClassName },
        React.createElement(AddAttachmentsBrushawayPanel, { items: items, isUploading: isUploading, aggregateProgress: aggregateProgress, onFilesSelected: onFilesSelected, onNameChange: onNameChange, onRemoveItem: onRemoveItem, onRetryItem: onRetryItem, onRetryFailed: onRetryFailed, onCancelUpload: onCancelUpload, onSubmit: onSubmit, maxSizeBytes: maxSizeBytes, accept: accept, columns: columns, labels: labels, discardConfirmOpen: discardConfirmOpen, onCancelDiscard: handleCancelDiscard, onConfirmDiscard: handleConfirmDiscard, onRequestClose: requestClose })));
}
